import type {
  ChatResolveDraftAction,
  ChatResolveDraftNote,
  ChatResolveEntityOption,
  EditableResolveDraftAction,
  EditableResolveDraftNote,
} from '../../models/admin/AdminChatResolveDraft';
import { defaultDueAtFields, dueAtFieldsToIso } from '../../utils/chatResolveDueAt';

export function toEditableNotes(notes: ChatResolveDraftNote[]): EditableResolveDraftNote[] {
  return notes.map((note) => ({
    ...note,
    selected: true,
    dueAt: '',
    dueAtInputType: 'date' as const,
  }));
}

export function toEditableActions(actions: ChatResolveDraftAction[]): EditableResolveDraftAction[] {
  return actions.map((action) => {
    const dueFields = defaultDueAtFields(action.suggestedDueAt, action.dueAtConfidence);
    return {
      ...action,
      selected: true,
      dueAt: dueFields.dueAt,
      dueAtInputType: dueFields.dueAtInputType,
    };
  });
}

export function countSelectedResolveDrafts(
  notes: EditableResolveDraftNote[],
  actions: EditableResolveDraftAction[],
): { notes: number; actions: number } {
  return {
    notes: notes.filter((note) => note.selected && note.content.trim()).length,
    actions: actions.filter((action) => action.selected && action.title.trim()).length,
  };
}

export interface ResolveDraftCommitPayload {
  selectedNotes: Array<{
    content: string;
    modelType: string;
    modelExternalId: string;
    dueAt: null;
  }>;
  selectedActions: Array<{
    title: string;
    description: string | null;
    modelType: string | null;
    modelExternalId: string | null;
    dueAt: string | null;
  }>;
}

export function buildSelectedResolveDraftPayload(
  notes: EditableResolveDraftNote[],
  actions: EditableResolveDraftAction[],
): ResolveDraftCommitPayload {
  const selectedNotes = notes
    .filter((note) => note.selected && note.content.trim())
    .map((note) => ({
      content: note.content.trim(),
      modelType: note.modelType,
      modelExternalId: note.modelExternalId,
      dueAt: null,
    }));

  const selectedActions = actions
    .filter((action) => action.selected && action.title.trim())
    .map((action) => ({
      title: action.title.trim(),
      description: action.description?.trim() || null,
      modelType: action.modelType,
      modelExternalId: action.modelExternalId,
      dueAt: dueAtFieldsToIso(action.dueAt, action.dueAtInputType),
    }));

  return { selectedNotes, selectedActions };
}

export function validateResolveDraftCommitSelection(
  notes: EditableResolveDraftNote[],
  actions: EditableResolveDraftAction[],
): string | null {
  const { selectedNotes, selectedActions } = buildSelectedResolveDraftPayload(notes, actions);

  if (!selectedNotes.length && !selectedActions.length) {
    return 'Select at least one note or action to save.';
  }

  const missingActionDue = actions.some(
    (action) => action.selected && action.title.trim() && !action.dueAt,
  );
  if (missingActionDue) {
    return 'Add a due date for each selected action before saving.';
  }

  return null;
}

export function updateNoteEntityInList(
  notes: EditableResolveDraftNote[],
  draftId: string,
  option: ChatResolveEntityOption,
): EditableResolveDraftNote[] {
  return notes.map((note) =>
    note.draftId === draftId
      ? {
          ...note,
          modelType: option.modelType,
          modelExternalId: option.modelExternalId,
          modelLabel: option.label,
        }
      : note,
  );
}

export function updateActionEntityInList(
  actions: EditableResolveDraftAction[],
  draftId: string,
  option: ChatResolveEntityOption | null,
): EditableResolveDraftAction[] {
  return actions.map((action) =>
    action.draftId === draftId
      ? option
        ? {
            ...action,
            modelType: option.modelType,
            modelExternalId: option.modelExternalId,
            modelLabel: option.label,
          }
        : {
            ...action,
            modelType: null,
            modelExternalId: null,
            modelLabel: null,
          }
      : action,
  );
}

export function getActionAttachToSelectValue(
  action: Pick<EditableResolveDraftAction, 'modelType' | 'modelExternalId'>,
  attachToOptions: ChatResolveEntityOption[],
): string {
  if (action.modelType && action.modelExternalId) {
    return `${action.modelType}:${action.modelExternalId}`;
  }

  const contactOption = attachToOptions.find((option) => option.modelType === 'CONTACT');
  return contactOption ? `CONTACT:${contactOption.modelExternalId}` : '';
}

export function formatSuggestedInvestmentLabel(modelLabel: string | null): string | null {
  if (!modelLabel) return null;
  return modelLabel.replace(/^Investment:\s*/i, '');
}

export function getActionDueDateHelperText(
  dueAtConfidence: EditableResolveDraftAction['dueAtConfidence'],
  dueAtInputType: EditableResolveDraftAction['dueAtInputType'],
): string {
  if (dueAtConfidence === 'high' || dueAtConfidence === 'medium') {
    return dueAtInputType === 'datetime-local'
      ? 'Suggested time from chat — adjust if needed'
      : 'Suggested from chat — adjust if needed';
  }

  return 'Required when approving an action';
}
